import React from "react";
import PropTypes from "prop-types";
import styled, { css, keyframes } from "styled-components";

import Button from "./Button.jsx";
import SectionActionButton from "./SectionActionButton";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SpinRing = styled.span`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  padding: 4px;
  pointer-events: none;
  overflow: hidden;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;

  &::before {
    content: "";
    position: absolute;
    inset: -1000%;
    background: conic-gradient(
      from 90deg at 50% 50%,
      #25a1ee 0%,
      #03324f 50%,
      #25a1ee 100%
    );
    animation: ${spin} 2s linear infinite;
    will-change: transform;
    transform: translateZ(0);
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const borderMagicStyles = css`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border: 0;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    border: 0;
  }
`;

const MagicButton = styled(Button)`
  ${borderMagicStyles}
`;

const MagicSectionButton = styled(SectionActionButton)`
  ${borderMagicStyles}
`;

const withSpinRing = (StyledButton) => {
  const BorderMagicButton = React.forwardRef(
    ({ children, as, forwardedAs, ...props }, ref) => (
      <StyledButton ref={ref} forwardedAs={as || forwardedAs} {...props}>
        <SpinRing aria-hidden="true" />
        {children}
      </StyledButton>
    ),
  );

  BorderMagicButton.displayName = "BorderMagicButton";

  BorderMagicButton.propTypes = {
    children: PropTypes.node.isRequired,
    as: PropTypes.elementType,
    forwardedAs: PropTypes.elementType,
  };

  return BorderMagicButton;
};

const BorderMagicButton = withSpinRing(MagicButton);
export const SectionBorderMagicButton = withSpinRing(MagicSectionButton);

export default BorderMagicButton;
